import { auth } from '../firebase/firebase';

const getBaseUrl = () => {
  if (typeof window === 'undefined') return 'https://tufirma.pro';

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // 🛡️ VPS & DEV DETECTION
  if (hostname.includes('vps') || hostname === 'localhost' || hostname.includes('127.0.0.1')) {
    // If we are on the VPS, we need to point to the port 3001
    // We strictly use the current hostname but swapping the port if it's the 3000 one.
    const base = hostname.replace('3000', '3001');
    
    // For localhost we usually need the explicit port
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//${hostname}:3001`;
    }
    
    // For VPS, the port might be part of the subdomain or explicit
    return `${protocol}//${base}`;
  }

  // 🌍 PRODUCTION OVERRIDE
  if (import.meta.env.VITE_NEXT_PUBLIC_URL) {
    return import.meta.env.VITE_NEXT_PUBLIC_URL;
  }

  return 'https://tufirma.pro';
};

const PUBLIC_API_URL = getBaseUrl();

/**
 * 🌐 API CLIENT
 * High-performance network handler for the Sales Machine.
 */
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  }

  private async request(path: string, options: RequestInit = {}) {
    const token = await this.getAuthToken();
    const headers = new Headers(options.headers || {});

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const fullUrl = `${this.baseUrl}${cleanPath}`;

    if (process.env.NODE_ENV === 'development') {
        console.log(`[ApiClient] 🛰️ ${options.method || 'GET'} -> ${fullUrl}`);
    }

    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API Error: ${response.statusText}`);
    }

    return await response.json();
  }

  async post(path: string, body: any) {
    return this.request(path, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async patch(path: string, body: any) {
    return this.request(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async delete(path: string, body: any) {
    return this.request(path, {
      method: 'DELETE',
      body: JSON.stringify(body),
    });
  }

  async get(path: string) {
    return this.request(path, {
      method: 'GET',
    });
  }
}

export const apiClient = new ApiClient(PUBLIC_API_URL);
